import { useState, useEffect, useRef, useCallback } from "react";
import {
  Check,
  CircleCheck,
  Download,
  FileText,
  LoaderCircle,
  Pencil,
  RefreshCw,
  RotateCcw,
  TriangleAlert,
  Upload,
} from "lucide-react";
import API_BASE_URL from "../config";
import TakeoffImageEditor, { confidenceBadgeClass } from "./TakeoffImageEditor";
import HatchSwatch from "./HatchSwatch";
import LegendReviewCard from "./LegendReviewCard";
import ImageLightbox, { useImageLightbox } from "./ImageLightbox";
import { isRunningStatus, isFailureStatus, isCancelledStatus, isAwaitingInputStatus } from "../utils/jobStatus";
import { statusBadgeClass, statusLabel } from "../utils/statusBadge";
import useJobWaiter from "../hooks/useJobWaiter";
import "./TakeoffWorkflow.css";

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY_MS = 3000;
// BoQ export targets offered once a result has positions (backend
// /wall-extraction/{job}/export?format=…).
const EXPORT_FORMATS = [
  { format: "xlsx", label: "Excel", title: "Download the BoQ as an Excel workbook" },
  { format: "docx", label: "Word", title: "Download the BoQ as a Word document" },
  { format: "pdf", label: "PDF", title: "Download the BoQ as a PDF" },
];

export default function TakeoffWorkflow({ onStatusChange, reattachJob = null, onReattached }) {
  const { waitFor } = useJobWaiter();
  const [phase, setPhase] = useState("idle");
  // One or more PDFs for a single logical plan set — e.g. ground floor, first
  // floor, and a section/height drawing each delivered as separate files. The
  // backend merges >1 files into one combined takeoff job (see
  // create_and_start_combined_job); a single file behaves exactly as before.
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const [docFilename, setDocFilename] = useState("");
  const [jobId, setJobId] = useState(null);
  const [streamEvents, setStreamEvents] = useState([]);
  const [streamImages, setStreamImages] = useState([]); // [{label, url}] — shown during streaming
  const [awaitingStreamData, setAwaitingStreamData] = useState(false);
  const [heartbeatSeen, setHeartbeatSeen] = useState(false);
  const [isStoppingJob, setIsStoppingJob] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null); // from done event
  const [floors, setFloors] = useState([]); // fetched after stream completes
  const [isFetchingFloors, setIsFetchingFloors] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState(null); // floor currently open in editor
  const [showEditor, setShowEditor] = useState(false);
  const [isRecomputingFloor, setIsRecomputingFloor] = useState(false);
  const [isPreviewingFloor, setIsPreviewingFloor] = useState(false);
  const [floorMaterialMasks, setFloorMaterialMasks] = useState([]); // [{key,label,color,url}]
  const [referenceImageUrl, setReferenceImageUrl] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [measurements, setMeasurements] = useState(null); // {wall_count, total_gross_m2, floor_height_m, by_material}
  const [measurementsStatus, setMeasurementsStatus] = useState("saved"); // "saved" | "estimate" | "loading"
  const [toolMode, setToolMode] = useState("select");
  const [history, setHistory] = useState([]);
  const [isFetchingHistory, setIsFetchingHistory] = useState(false);
  const [isLoadingHistoryJob, setIsLoadingHistoryJob] = useState(false);
  const [isRefetchingResult, setIsRefetchingResult] = useState(false);
  const [isRefetchingImages, setIsRefetchingImages] = useState(false);
  const [streamKey, setStreamKey] = useState(0);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [editedFloorHeights, setEditedFloorHeights] = useState({});
  const [isRecomputingAllFloors, setIsRecomputingAllFloors] = useState(false);
  // Set when a validation reason ({source_page, bbox_pct}) is clicked -
  // TakeoffImageEditor resolves it to a wall selection once its own
  // pageIndex/detectedWalls reflect whatever floor handleJumpToReason
  // switched to, then clears this via onWallFocused.
  const [pendingWallFocus, setPendingWallFocus] = useState(null);
  const { lightbox, openLightbox, closeLightbox, stepLightbox } = useImageLightbox();

  const editorRef = useRef(null);
  const esRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const previewSeqRef = useRef(0); // guards against out-of-order preview responses
  // Always the current selectedFloor, readable inside async callbacks that
  // started against a different (possibly now-closed) floor — lets an
  // in-flight recompute detect that the user has since switched floors and
  // skip clobbering whatever is actually open now.
  const selectedFloorRef = useRef(null);
  useEffect(() => {
    selectedFloorRef.current = selectedFloor;
  }, [selectedFloor]);

  const toEditorLayers = useCallback(
    (materialMasks) =>
      (materialMasks || []).map((m) => ({
        key: m.layer_key,
        label: m.label,
        color: m.color,
        hatchFeatures: m.hatch_features,
        swatchImageB64: m.swatch_image_b64,
        url: `data:image/png;base64,${m.mask_b64}`,
      })),
    [],
  );

  const resolvePreviewImageUrl = useCallback((payload) => {
    if (!payload) return null;
    const relativeUrl = payload.preview_url || payload.image_url || payload.url;
    if (relativeUrl) {
      if (relativeUrl.startsWith("http")) return relativeUrl;
      return `${API_BASE_URL}${relativeUrl}`;
    }
    const b64 = payload.preview_b64 || payload.image_b64 || payload.mask_b64;
    if (b64) return `data:image/png;base64,${b64}`;
    return null;
  }, []);

  // ── Notify parent card ────────────────────────────────
  useEffect(() => {
    if (!onStatusChange) return;
    if (phase === "idle" || phase === "failed") {
      onStatusChange("idle", "Idle");
      return;
    }
    if (phase === "completed") {
      onStatusChange("idle", "Completed");
      return;
    }
    const eventCount = streamEvents.length;
    onStatusChange("running", `Running · ${eventCount} events`);
  }, [phase, streamEvents, onStatusChange]);

  // ── Clean up EventSource on unmount ───────────────────
  useEffect(() => {
    return () => esRef.current?.close();
  }, []);

  // ── File validation ────────────────────────────────────
  // Accepts a FileList (or array) — one file behaves exactly as before; more
  // than one is a plan set split across sheets (see `files` state comment).
  const applyFiles = (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    for (const f of list) {
      if (!f.name.toLowerCase().endsWith(".pdf")) {
        setError(`${f.name}: only PDF files are allowed`);
        return;
      }
      if (f.size > MAX_UPLOAD_BYTES) {
        setError(`${f.name}: file too large (max 50 MB)`);
        return;
      }
    }
    setFiles(list);
    setError(null);
  };

  const handleFileChange = (e) => applyFiles(e.target.files);
  const handleDrop = (e) => {
    e.preventDefault();
    applyFiles(e.dataTransfer.files);
  };

  // ── Upload ─────────────────────────────────────────────
  const handleUpload = async () => {
    if (!files.length) return;
    setPhase("uploading");
    setError(null);
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    formData.append("document_type", "architectural_map");
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/documents/upload`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Upload failed");
      }
      const docs = await res.json();
      // The upload itself already started the takeoff job (single file: its own
      // job; multiple files: one combined job — see create_and_start_combined_job)
      // and every entry shares the same job_id, so go straight to streaming
      // instead of a separate /wall-extraction/start round-trip.
      const jobId = docs[0]?.job_id;
      if (!jobId) throw new Error("Upload succeeded but no job was started");
      setDocFilename(files.map((f) => f.name).join(" + "));
      setJobId(jobId);
      setPhase("streaming");
    } catch (err) {
      setError(err.message);
      setPhase("failed");
    }
  };

  // No separate job-status polling: this workflow's own dedicated SSE stream
  // (below) is its sole, sufficient progress channel — the missed-terminal-
  // event race that a polling safety-net would guard against is instead
  // closed at the source, in the stream endpoint itself (it now checks the
  // persisted job store before falling back to its live event queue, so a
  // reconnect always resolves correctly with no client-side polling needed).

  // ── SSE stream ─────────────────────────────────────────
  useEffect(() => {
    if (phase !== "streaming" || !jobId) return;
    setAwaitingStreamData(true);
    setHeartbeatSeen(false);

    const es = new EventSource(
      `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/stream`,
      { withCredentials: true },
    );
    esRef.current = es;

    es.onmessage = (e) => {
      let payload;
      try {
        payload = JSON.parse(e.data);
      } catch {
        return;
      }

      // Any received message (heartbeat included) proves the connection is alive,
      // so the retry budget counts only *consecutive* failures — otherwise a long
      // job that survived a few scattered reconnects would be killed mid-run.
      if (reconnectAttemptsRef.current !== 0) {
        reconnectAttemptsRef.current = 0;
        setReconnectAttempt(0);
      }

      // Keep showing the loader while the stream only sends heartbeat keep-alives.
      if (payload.type === "heartbeat") {
        setHeartbeatSeen(true);
        return;
      }

      if (payload.type === "stage") {
        setAwaitingStreamData(false);
        setHeartbeatSeen(false);
        // Track this event
        setStreamEvents((prev) => [
          ...prev,
          {
            type: "stage",
            stage: payload.stage,
            message: payload.message || payload.stage,
            timestamp: new Date().toLocaleTimeString(),
          },
        ]);
      } else if (payload.type === "image") {
        setAwaitingStreamData(false);
        setHeartbeatSeen(false);
        // Track this event
        setStreamEvents((prev) => [
          ...prev,
          {
            type: "image",
            message: payload.label || "Image received",
            timestamp: new Date().toLocaleTimeString(),
          },
        ]);
        const url = payload.url ? `${API_BASE_URL}${payload.url}` : null;
        if (url) {
          setStreamImages((prev) => [...prev, { label: payload.label, url }]);
        }
      } else if (payload.type === "done") {
        setAwaitingStreamData(false);
        setHeartbeatSeen(false);
        // Track this event
        setStreamEvents((prev) => [
          ...prev,
          {
            type: "done",
            message: payload.message || "Extraction complete",
            timestamp: new Date().toLocaleTimeString(),
          },
        ]);
        if (payload.result) {
          setExtractionResult(payload.result);
        }
        es.close();
        esRef.current = null;
        setPhase("completed");
      } else if (payload.type === "error") {
        setAwaitingStreamData(false);
        setHeartbeatSeen(false);
        setError(payload.message || "Extraction error");
        es.close();
        esRef.current = null;
        setPhase("failed");
      } else if (payload.type === "cancelled") {
        // Real confirmation that the backend actually stopped the job (Celery
        // task revoked / cooperative checkpoint) — not the optimistic reset
        // handleStopJob used to do the instant it fired the request. Reuses the
        // "failed" phase's existing error-banner UI with a non-alarming message.
        setAwaitingStreamData(false);
        setHeartbeatSeen(false);
        setError(payload.message || "Extraction stopped by user.");
        setIsStoppingJob(false);
        es.close();
        esRef.current = null;
        setPhase("failed");
      } else if (payload.type === "awaiting_legend_approval") {
        // Phase 1 (legend + floor heights, fast) finished — pause here and
        // let the user review/confirm the legend before Phase 2 (the
        // expensive per-wall CV+VLM classification) is even enqueued.
        setAwaitingStreamData(false);
        setHeartbeatSeen(false);
        setStreamEvents((prev) => [
          ...prev,
          {
            type: "awaiting_legend_approval",
            message: payload.message || "Legend ready for review",
            timestamp: new Date().toLocaleTimeString(),
          },
        ]);
        es.close();
        esRef.current = null;
        setPhase("awaiting_legend_approval");
      }
    };

    es.onerror = () => {
      setAwaitingStreamData(false);
      setHeartbeatSeen(false);
      es.close();
      esRef.current = null;

      reconnectAttemptsRef.current += 1;
      const attempt = reconnectAttemptsRef.current;

      setPhase((prev) => {
        if (prev !== "streaming") return prev;

        if (attempt <= MAX_RECONNECT_ATTEMPTS) {
          setReconnectAttempt(attempt);
          reconnectTimerRef.current = setTimeout(() => {
            setStreamKey((k) => k + 1);
            setAwaitingStreamData(true);
          }, RECONNECT_DELAY_MS);
          return prev; // stay in streaming; useEffect will re-run when streamKey changes
        }

        setError(
          "Stream connection lost after multiple retries. The job may still be running in the background — check history to re-attach.",
        );
        return "failed";
      });
    };

    return () => {
      es.close();
      esRef.current = null;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };
  }, [phase, jobId, streamKey]); // streamKey bumps on each reconnect attempt

  // ── Stop running job ───────────────────────────────────
  const handleStopJob = useCallback(async () => {
    if (!jobId || isStoppingJob) return;
    setIsStoppingJob(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/jobs/${jobId}/cancel`,
        { method: "POST" },
      );
      // 409 means the job already reached a terminal state (finished/failed just
      // before the cancel landed) — not a real failure to stop it.
      if (!res.ok && res.status !== 409) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload.detail || "Failed to stop job");
      }
      // Do NOT close the stream or reset here. Unlike the old DELETE endpoint
      // (which only released a document lock and left the worker running), this
      // actually revokes the Celery task — but the confirmation that it really
      // stopped arrives as a "cancelled" SSE event (see the stream handler
      // above), so the UI stays truthful instead of optimistically declaring
      // success the instant the request is sent.
    } catch (err) {
      setError(err.message || "Failed to stop job");
      setPhase("failed");
      setIsStoppingJob(false);
    }
  }, [jobId, isStoppingJob]);

  // ── Fetch floors after stream completes ───────────────
  const fetchFloors = useCallback(async () => {
    if (!jobId) return;
    setIsFetchingFloors(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/floors`,
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Failed to fetch floors");
      }
      const data = await res.json();
      setFloors(data.floors || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsFetchingFloors(false);
    }
  }, [jobId]);

  // ── Trigger floors fetch when stream finishes ─────────
  useEffect(() => {
    if (phase === "completed" && jobId) fetchFloors();
  }, [phase, jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Legend edit committed: refresh the result summary + every floor's
  // recomputed quantities (a legend recompute can change any floor's totals,
  // not just one) ──────────────────────────────────────────────────────────
  const handleLegendRecomputed = useCallback(
    (data) => {
      if (data?.result) setExtractionResult(data.result);
      fetchFloors();
    },
    [fetchFloors],
  );

  // ── Legend confirmed (approval-gate mode): Phase 2 (wall extraction) was
  // just enqueued -- hand control back to the main SSE stream for its
  // progress, exactly like the initial post-upload flow. Reusing the same
  // "streaming" phase re-triggers the existing stream-opening effect
  // ([phase, jobId, streamKey] deps) with no other plumbing needed.
  const handleLegendConfirmed = useCallback(() => {
    setAwaitingStreamData(true);
    setPhase("streaming");
  }, []);

  // ── Open a floor in the editor ─────────────────────────
  const handleOpenFloorInEditor = useCallback(
    (floor) => {
      // Prefer the region-matched reference crop (spans exactly the mask extent, so
      // the mask overlays it 1:1 in the editor). Fall back to a page streamImage for
      // older runs whose context predates reference_b64 (alignment is approximate).
      const ctxRef = floor.context?.reference_b64;
      const pageNum = floor.page_index;
      const labelMatch = streamImages.find((img) =>
        img.label?.toLowerCase().includes(`p${pageNum}`),
      );
      const bgImage =
        labelMatch || streamImages[pageNum - 1] || streamImages[0] || null;

      setSelectedFloor(floor);
      setFloorMaterialMasks(toEditorLayers(floor.material_masks));
      setReferenceImageUrl(
        ctxRef ? `data:image/png;base64,${ctxRef}` : (bgImage?.url ?? null),
      );
      setPreviewImageUrl(null);
      setMeasurements(floor.quantities || null);
      setMeasurementsStatus("saved");
      setError(null);
      setShowEditor(true);
    },
    [streamImages, toEditorLayers],
  );

  // ── Jump to the wall/floor a validation reason points at ───────────────
  const handleJumpToReason = useCallback(
    (reason) => {
      if (reason?.source_page == null) return;
      const floor = floors.find((f) => f.page_index === reason.source_page);
      if (!floor) return;
      if (floor.floor_id !== selectedFloor?.floor_id) {
        handleOpenFloorInEditor(floor);
      }
      setPendingWallFocus(
        reason.bbox_pct
          ? { source_page: reason.source_page, bbox_pct: reason.bbox_pct }
          : null,
      );
    },
    [floors, selectedFloor, handleOpenFloorInEditor],
  );
  const handleWallFocused = useCallback(() => setPendingWallFocus(null), []);

  // ── Close the floor editor ─────────────────────────────
  const handleCloseEditor = useCallback(() => {
    if (
      editorRef.current?.hasUnsavedEdits() &&
      !window.confirm(
        "This floor has edits that were never recomputed — closing now discards them. Close anyway?",
      )
    ) {
      return;
    }
    setShowEditor(false);
    setSelectedFloor(null);
    setPreviewImageUrl(null);
    setFloorMaterialMasks([]);
    setMeasurements(null);
  }, []);

  // ── Recompute floor with edited mask ──────────────────
  // The editor stays open on success (recompute is repeatable — Close is the
  // explicit exit) and refreshes its layers/measurements from the response.
  // Guarded against the user switching to a different floor while this
  // request is in flight: only the floor-specific state (measurements,
  // preview, selected floor/masks, the editor's dirty flag) is skipped in
  // that case — the backend write and the shared floors list still apply.
  // Confirms first — this fires a full (VLM-based) re-detection, not a free
  // local estimate like Preview.
  const handleRecomputeFloor = useCallback(async () => {
    if (!editorRef.current || !jobId || !selectedFloor) return;
    if (
      !window.confirm(
        `Recompute ${selectedFloor.floor_label || "this floor"}? This re-runs full AI-based wall/material detection and may take a while.`,
      )
    )
      return;
    const recomputingFloorId = selectedFloor.floor_id;
    const isStillOpen = () =>
      selectedFloorRef.current?.floor_id === recomputingFloorId;
    setIsRecomputingFloor(true);
    setError(null);
    try {
      const payload = await editorRef.current.getMaskPayload();
      const res = await fetch(
        `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/floors/${recomputingFloorId}/recompute`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Recompute failed");
      }
      let data = await res.json().catch(() => null);
      if (data?.job_id) {
        const job = await waitFor(data.job_id);
        if (job.status === "failed") throw new Error(job.error || "Recompute failed");
        data = job.result;
      }
      if (data?.result) setExtractionResult(data.result);
      if (isStillOpen()) {
        if (data?.floor) {
          setMeasurements(data.floor);
          setMeasurementsStatus("saved");
        }
        setPreviewImageUrl(null);
        editorRef.current?.markSaved();
      }

      // Floors listing is the source of truth for masks/quantities — refresh
      // it, then refresh the still-open editor from the updated entry (only
      // if the user hasn't since switched to a different floor).
      const floorsRes = await fetch(
        `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/floors`,
      );
      if (floorsRes.ok) {
        const floorsData = await floorsRes.json().catch(() => null);
        const nextFloors = floorsData?.floors || [];
        setFloors(nextFloors);
        const refreshed = nextFloors.find(
          (f) => f.floor_id === recomputingFloorId,
        );
        if (refreshed && isStillOpen()) {
          setSelectedFloor(refreshed);
          setFloorMaterialMasks(toEditorLayers(refreshed.material_masks));
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRecomputingFloor(false);
    }
  }, [jobId, selectedFloor, toEditorLayers, waitFor]);

  // ── Recompute all floors with updated floor heights ───
  const handleRecomputeAllFloors = useCallback(async () => {
    if (!jobId || floors.length === 0 || isRecomputingAllFloors) return;
    if (
      !window.confirm(
        `Recompute all ${floors.length} floor(s) with the current floor heights? This re-runs full AI-based detection for every floor and may take a while.`,
      )
    )
      return;
    setIsRecomputingAllFloors(true);
    setError(null);
    try {
      // Dispatched sequentially, not in parallel: the recompute lock is
      // scoped to the whole job (not per floor), so parallel dispatch would
      // just contend for the same lock instead of actually running concurrently.
      for (const floor of floors) {
        const res = await fetch(
          `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/floors/${floor.floor_id}/recompute`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ floor_heights: editedFloorHeights }),
          },
        );
        if (!res.ok) {
          const detail = await res.json().catch(() => ({}));
          throw new Error(detail.detail || `Floor ${floor.floor_label} recompute failed`);
        }
        const data = await res.json().catch(() => null);
        if (data?.job_id) {
          const job = await waitFor(data.job_id);
          if (job.status === "failed") {
            throw new Error(job.error || `Floor ${floor.floor_label} recompute failed`);
          }
        }
      }
      // Reload result to reflect recalculated areas
      const resultRes = await fetch(
        `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/result`,
      );
      if (resultRes.ok) {
        const data = await resultRes.json().catch(() => null);
        if (data) setExtractionResult(data.result ?? data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRecomputingAllFloors(false);
    }
  }, [jobId, floors, editedFloorHeights, isRecomputingAllFloors, waitFor]);

  // ── Preview floor mask ────────────────────────────────
  // No-VLM geometry-only estimate — used both by the manual "Preview Floor"
  // button and the editor's debounced auto-estimate after edits. A sequence
  // guard drops a response if a newer preview request has since been fired
  // (the editor can call this again before a slow one resolves).
  const handlePreviewFloor = useCallback(async () => {
    if (!editorRef.current || !jobId || !selectedFloor || isPreviewingFloor)
      return;
    const previewingFloorId = selectedFloor.floor_id;
    setIsPreviewingFloor(true);
    setMeasurementsStatus("loading");
    setError(null);
    const seq = ++previewSeqRef.current;
    try {
      const payload = await editorRef.current.getMaskPayload();
      const res = await fetch(
        `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/floors/${previewingFloorId}/preview`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Preview failed");
      }
      const data = await res.json().catch(() => ({}));
      // Drop this response if a newer preview has since superseded it, or if
      // the user switched to a different floor while it was in flight — in
      // either case it's no longer safe to apply to what's on screen now.
      if (seq !== previewSeqRef.current) return;
      if (selectedFloorRef.current?.floor_id !== previewingFloorId) return;
      setMeasurements(data);
      setMeasurementsStatus("estimate");
      const url = resolvePreviewImageUrl(data);
      if (url) setPreviewImageUrl(url);
    } catch (err) {
      if (seq === previewSeqRef.current) {
        setError(err.message || "Failed to generate floor preview");
        setMeasurementsStatus("saved");
      }
    } finally {
      if (seq === previewSeqRef.current) setIsPreviewingFloor(false);
    }
  }, [isPreviewingFloor, jobId, selectedFloor, resolvePreviewImageUrl]);

  // ── Sync floor heights into editable state ────────────
  useEffect(() => {
    if (extractionResult?.floor_heights) {
      setEditedFloorHeights({ ...extractionResult.floor_heights });
    }
  }, [extractionResult]);

  // ── History: fetch list ────────────────────────────────
  const fetchHistory = useCallback(async () => {
    setIsFetchingHistory(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/construction/wall-extraction/history`,
      );
      if (!res.ok) return;
      const data = await res.json();
      setHistory(
        Array.isArray(data)
          ? data
          : (data.jobs ?? data.runs ?? data.history ?? []),
      );
    } catch (_) {
      // history is supplementary — never block the main flow
    } finally {
      setIsFetchingHistory(false);
    }
  }, []);

  // ── History: load a specific past job ─────────────────
  const loadHistoryJob = useCallback(async (item) => {
    setIsLoadingHistoryJob(true);
    setError(null);
    try {
      // A reconciled/interrupted run comes back as a failure token ("error"); route
      // it to the failed view instead of re-opening a stream that will 404 (its
      // in-memory job + SSE queue are gone), which is what left resume spinning.
      // A cancelled run never persisted a result either — same treatment, distinct
      // message (it was stopped deliberately, not a failure).
      const isRunning = isRunningStatus(item.status);
      const isFailed = isFailureStatus(item.status);
      const isCancelled = isCancelledStatus(item.status);
      const isAwaitingLegend = isAwaitingInputStatus(item.status);
      if (isFailed) {
        setError(item.error || item.message || "Extraction failed");
      } else if (isCancelled) {
        setError(item.message || "Extraction was stopped by user.");
      }

      // Always fetch saved images — they exist for both running and completed jobs
      const imagesRes = await fetch(
        `${API_BASE_URL}/api/v1/construction/wall-extraction/${item.job_id}/images`,
      );
      const imagesData = imagesRes.ok
        ? await imagesRes.json().catch(() => null)
        : null;
      const rawImages = imagesData
        ? Array.isArray(imagesData)
          ? imagesData
          : (imagesData.images ?? [])
        : [];

      setStreamImages(
        rawImages.map((img) => ({
          label: img.label,
          url: img.url?.startsWith("http")
            ? img.url
            : `${API_BASE_URL}${img.url}`,
        })),
      );

      if (!isRunning && !isFailed && !isCancelled && !isAwaitingLegend) {
        // Completed job — fetch stored result
        const resultRes = await fetch(
          `${API_BASE_URL}/api/v1/construction/wall-extraction/${item.job_id}/result`,
        );
        const resultData = resultRes.ok
          ? await resultRes.json().catch(() => null)
          : null;
        if (resultData) setExtractionResult(resultData.result ?? resultData);
      }

      setDocFilename(
        item.source_file ?? item.filename ?? item.document_id ?? item.job_id,
      );

      // Reset reconnect state so the fresh stream starts clean
      reconnectAttemptsRef.current = 0;
      setReconnectAttempt(0);
      setStreamKey(0);

      setJobId(item.job_id);
      setPhase(
        isFailed || isCancelled
          ? "failed"
          : isRunning
            ? "streaming"
            : isAwaitingLegend
              ? "awaiting_legend_approval"
              : "completed",
      );
    } catch (err) {
      setError(err.message ?? "Failed to load extraction history item");
    } finally {
      setIsLoadingHistoryJob(false);
    }
  }, []);

  // ── Reattach to a run opened from the global activity tray ─
  useEffect(() => {
    if (!reattachJob) return;
    loadHistoryJob(reattachJob); // routes running→streaming, failed→failed, awaiting-legend→awaiting_legend_approval, done→completed
    onReattached?.(); // clear the parent's pending-reattach so it fires once
  }, [reattachJob, loadHistoryJob, onReattached]);

  // ── Reload stored result for current job ──────────────
  const handleRefetchResult = useCallback(async () => {
    if (!jobId) return;
    setIsRefetchingResult(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/result`,
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Failed to load result");
      }
      const data = await res.json();
      setExtractionResult(data.result ?? data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRefetchingResult(false);
    }
  }, [jobId]);

  // ── Reload saved images for current job ───────────────
  const handleRefetchImages = useCallback(async () => {
    if (!jobId) return;
    setIsRefetchingImages(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/images`,
      );
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "Failed to load images");
      }
      const data = await res.json();
      const rawImages = Array.isArray(data) ? data : (data.images ?? []);
      setStreamImages(
        rawImages.map((img) => ({
          label: img.label,
          url: img.url?.startsWith("http")
            ? img.url
            : `${API_BASE_URL}${img.url}`,
        })),
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRefetchingImages(false);
    }
  }, [jobId]);

  // ── Fetch history whenever idle ────────────────────────
  useEffect(() => {
    if (phase === "idle") fetchHistory();
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reset ──────────────────────────────────────────────
  const reset = () => {
    esRef.current?.close();
    esRef.current = null;
    setPhase("idle");
    setFiles([]);
    setError(null);
    setDocFilename("");
    setJobId(null);
    setStreamEvents([]);
    setStreamImages([]);
    setAwaitingStreamData(false);
    setHeartbeatSeen(false);
    setExtractionResult(null);
    setFloors([]);
    setIsFetchingFloors(false);
    setSelectedFloor(null);
    setShowEditor(false);
    setIsRecomputingFloor(false);
    setIsPreviewingFloor(false);
    setFloorMaterialMasks([]);
    setReferenceImageUrl(null);
    setPreviewImageUrl(null);
    setMeasurements(null);
    setMeasurementsStatus("saved");
    setIsStoppingJob(false);
    setIsLoadingHistoryJob(false);
    setIsRefetchingResult(false);
    setIsRefetchingImages(false);
    setEditedFloorHeights({});
    setIsRecomputingAllFloors(false);
    setStreamKey(0);
    setReconnectAttempt(0);
    reconnectAttemptsRef.current = 0;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  // ── Derived visibility ─────────────────────────────────
  const showUploadZone =
    (phase === "idle" || phase === "uploading") && !isLoadingHistoryJob;
  const showPipeline =
    phase !== "idle" && phase !== "failed" && phase !== "completed";
  const showThumbnails =
    (phase === "streaming" || phase === "completed") && streamImages.length > 0;
  const showCompleted = phase === "completed";

  return (
    <div className="twf-root">
      {/* Upload zone */}
      {showUploadZone && (
        <div
          className={`twf-dropzone${files.length ? " has-file" : ""}${phase === "uploading" ? " is-busy" : ""}`}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <input
            type="file"
            id="twf-input"
            accept=".pdf"
            multiple
            onChange={handleFileChange}
            disabled={phase === "uploading"}
            hidden
          />
          <label htmlFor="twf-input" className="twf-dropzone-label">
            {phase === "uploading" ? (
              <>
                <LoaderCircle size={20} className="ds-spin twf-dropzone-icon" aria-hidden="true" />
                <span className="twf-dropzone-title">Uploading…</span>
              </>
            ) : files.length ? (
              <>
                <FileText size={20} className="twf-dropzone-icon" aria-hidden="true" />
                <span className="twf-dropzone-title">
                  {files.map((f) => f.name).join(", ")}
                </span>
              </>
            ) : (
              <>
                <Upload size={20} className="twf-dropzone-icon" aria-hidden="true" />
                <span className="twf-dropzone-title">
                  Drop floor plan PDF(s) here or <u>browse</u>
                </span>
                <span className="twf-dropzone-hint">
                  PDF · max 50 MB each · select multiple files if the plan is
                  split across sheets (e.g. one PDF per floor + a section
                  drawing) — they'll be combined into one takeoff
                </span>
              </>
            )}
          </label>
          {files.length > 0 && phase === "idle" && (
            <button className="ds-btn ds-btn--primary" onClick={handleUpload}>
              Start extraction →
            </button>
          )}
        </div>
      )}

      {/* History job loading */}
      {isLoadingHistoryJob && (
        <div className="twf-stream-loader" role="status">
          <LoaderCircle size={16} className="ds-spin" aria-hidden="true" />
          <span>Loading extraction details…</span>
        </div>
      )}

      {/* History section (idle only) */}
      {phase === "idle" && !isLoadingHistoryJob && (
        <section className="twf-history-section">
          <div className="twf-section-header">
            <h3 className="ds-section-title ds-flush">Recent extractions</h3>
            {isFetchingHistory && (
              <LoaderCircle size={14} className="ds-spin twf-muted-icon" aria-label="Loading" />
            )}
          </div>
          {!isFetchingHistory && history.length === 0 && (
            <div className="ds-empty twf-empty">
              No previous extractions found.
            </div>
          )}
          {history.length > 0 && (
            <ul className="ds-list">
              {history.map((item) => (
                <li key={item.job_id} className="ds-list-row twf-history-item">
                  <div className="twf-history-item-body">
                    <div className="twf-history-item-info">
                      <span className="ds-list-title twf-history-filename">
                        {item.source_file ??
                          item.filename ??
                          item.document_id ??
                          item.job_id}
                      </span>
                      <span className={statusBadgeClass(item.status)}>
                        {statusLabel(item.status)}
                      </span>
                      {item.summary?.overall_confidence && (
                        <span
                          className={confidenceBadgeClass(item.summary.overall_confidence)}
                          title="Overall confidence the AI reports for this extraction — LOW means double-check the numbers before relying on them."
                        >
                          {item.summary.overall_confidence}
                        </span>
                      )}
                      {item.created_at && (
                        <span className="ds-meta">
                          {new Date(item.created_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                    {item.summary?.by_material &&
                      Object.keys(item.summary.by_material).length > 0 && (
                        <div className="twf-history-summary">
                          {Object.entries(item.summary.by_material).map(
                            ([mat, s]) => (
                              <span
                                key={mat}
                                className="ds-badge ds-mono"
                              >
                                {mat}: {s.net_m2?.toFixed(1) ?? "—"} m²
                              </span>
                            ),
                          )}
                        </div>
                      )}
                  </div>
                  <button
                    className="ds-btn ds-btn--secondary ds-btn--sm"
                    onClick={() => loadHistoryJob(item)}
                    disabled={
                      isLoadingHistoryJob ||
                      isFailureStatus(item.status)
                    }
                  >
                    View
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Error banner */}
      {error && (
        <div className="ds-alert ds-alert--danger ds-flush" role="alert">
          <span className="twf-alert-text">{error}</span>
          <button onClick={reset} className="ds-btn ds-btn--secondary ds-btn--sm">
            Try again
          </button>
        </div>
      )}

      {/* Stream events pipeline */}
      {showPipeline && (
        <section className="twf-pipeline">
          <div className="twf-pipeline-header">
            <span className="twf-live-dot" aria-hidden="true" />
            <span className="twf-pipeline-title">WF-03 — Plan quantity takeoff</span>
            {docFilename && (
              <span className="twf-pipeline-file ds-mono">{docFilename}</span>
            )}
          </div>
          <ol className="twf-pipeline-steps">
            {streamEvents.map((evt, i) => (
              <li key={i} className="twf-step">
                <span className="twf-step-icon" aria-hidden="true">
                  <Check size={12} />
                </span>
                <span className="twf-step-text">{evt.message || evt.type}</span>
                <span className="ds-meta twf-step-time">{evt.timestamp}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Live backend status + stop control while job is active */}
      {jobId && phase === "streaming" && (
        <div className="twf-job-status-row">
          <span className={statusBadgeClass("running")}>{statusLabel("running")}</span>
          <span className="twf-job-status-text">
            {streamEvents.length > 0
              ? streamEvents[streamEvents.length - 1].message
              : reconnectAttempt > 0
                ? `Reconnecting… (attempt ${reconnectAttempt} / ${MAX_RECONNECT_ATTEMPTS})`
                : "Waiting for backend status updates…"}
          </span>
          <button
            className="ds-btn ds-btn--danger"
            onClick={handleStopJob}
            disabled={isStoppingJob}
          >
            {isStoppingJob && <LoaderCircle size={14} className="ds-spin" aria-hidden="true" />}
            {isStoppingJob ? "Stopping…" : "Stop job"}
          </button>
        </div>
      )}

      {/* Loader while EventSource is connected but first event has not arrived yet */}
      {phase === "streaming" && awaitingStreamData && (
        <div className="twf-stream-loader" role="status">
          {heartbeatSeen ? (
            <span className="twf-heartbeat-indicator" aria-hidden="true" />
          ) : (
            <LoaderCircle size={16} className="ds-spin" aria-hidden="true" />
          )}
          <span>
            {heartbeatSeen
              ? "Heartbeat received… processing is still running"
              : reconnectAttempt > 0
                ? `Reconnecting… (attempt ${reconnectAttempt} / ${MAX_RECONNECT_ATTEMPTS})`
                : "Connecting to stream…"}
          </span>
        </div>
      )}

      {/* Intermediate image thumbnails */}
      {showThumbnails && (
        <section className="ds-card twf-card twf-thumbnail-strip">
          <h3 className="ds-section-title">Classified pages</h3>
          <div className="twf-thumbnails">
            {streamImages.map((img, i) => (
              <div key={i} className="twf-thumbnail">
                <img
                  src={img.url}
                  alt={img.label}
                  className="twf-thumbnail-img"
                  onClick={() => openLightbox(streamImages, i)}
                />
                <span className="twf-thumbnail-caption ds-mono">{img.label}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Floor editor — full-screen Blueprint Studio takeover */}
      {showEditor && selectedFloor && (
        <TakeoffImageEditor
          ref={editorRef}
          jobId={jobId}
          materialMasks={floorMaterialMasks}
          backgroundImageUrl={referenceImageUrl}
          toolMode={toolMode}
          onToolModeChange={setToolMode}
          floorLabel={selectedFloor?.floor_label ?? "Floor"}
          floorId={selectedFloor?.floor_id}
          pageIndex={selectedFloor?.page_index}
          previewImageUrl={previewImageUrl}
          maskContext={selectedFloor?.context}
          availableThicknessesMm={selectedFloor?.available_thicknesses_mm}
          measurements={measurements}
          measurementsStatus={measurementsStatus}
          overallConfidence={extractionResult?.overall_confidence}
          validation={extractionResult?.validation}
          lvComparison={extractionResult?.lv_comparison}
          onJumpToReason={handleJumpToReason}
          pendingWallFocus={pendingWallFocus}
          onWallFocused={handleWallFocused}
          isPreviewing={isPreviewingFloor}
          isRecomputing={isRecomputingFloor}
          errorMessage={error}
          onPreview={handlePreviewFloor}
          onRecompute={handleRecomputeFloor}
          onClose={handleCloseEditor}
          onPreviewImageClick={() =>
            openLightbox([{ url: previewImageUrl, label: "Preview" }], 0)
          }
        />
      )}

      {/* Completed — floors list */}
      {showCompleted && (
        <div className="twf-completed-section">
          <div className="ds-card twf-completed">
            <CircleCheck size={22} className="twf-completed-icon" aria-hidden="true" />
            <div className="twf-completed-text">
              <div className="twf-completed-title">Extraction complete</div>
              <div className="twf-completed-sub ds-mono">{docFilename}</div>
            </div>
            <div className="twf-completed-actions">
              <button
                className="ds-btn ds-btn--secondary"
                onClick={handleRefetchResult}
                disabled={isRefetchingResult || isRefetchingImages}
                title="Reload stored result from API"
              >
                {isRefetchingResult ? (
                  <LoaderCircle size={14} className="ds-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw size={14} aria-hidden="true" />
                )}
                {isRefetchingResult ? "Loading…" : "Load result"}
              </button>
              <button
                className="ds-btn ds-btn--secondary"
                onClick={handleRefetchImages}
                disabled={isRefetchingImages || isRefetchingResult}
                title="Reload saved images from API"
              >
                {isRefetchingImages ? (
                  <LoaderCircle size={14} className="ds-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw size={14} aria-hidden="true" />
                )}
                {isRefetchingImages ? "Loading…" : "Load images"}
              </button>
              {extractionResult?.positions?.length > 0 && jobId && (
                <div className="twf-export-row">
                  {EXPORT_FORMATS.map(({ format, label, title }) => (
                    <a
                      key={format}
                      className="ds-btn ds-btn--secondary"
                      href={`${API_BASE_URL}/api/v1/construction/wall-extraction/${jobId}/export?format=${format}`}
                      download
                      title={title}
                    >
                      <Download size={14} aria-hidden="true" />
                      {label}
                    </a>
                  ))}
                </div>
              )}
              <button className="ds-btn ds-btn--primary" onClick={reset}>
                New extraction
              </button>
            </div>
          </div>

          {extractionResult && (
            <div className="twf-result-section">
              {/* Top row: floor heights + legend side by side */}
              <div className="twf-result-info-row">
                {extractionResult.floor_heights &&
                  Object.keys(extractionResult.floor_heights).length > 0 && (
                    <section className="ds-card twf-card twf-result-card">
                      <div className="twf-section-header">
                        <h3 className="ds-section-title ds-flush">Floor heights</h3>
                        <button
                          className="ds-btn ds-btn--soft ds-btn--sm ds-push-end"
                          onClick={handleRecomputeAllFloors}
                          disabled={
                            isRecomputingAllFloors || floors.length === 0
                          }
                          title="Recompute all floors with updated heights — full AI-based detection for every floor, may take a while."
                        >
                          {isRecomputingAllFloors ? (
                            <LoaderCircle size={12} className="ds-spin" aria-hidden="true" />
                          ) : (
                            <RotateCcw size={12} aria-hidden="true" />
                          )}
                          {isRecomputingAllFloors ? "Recomputing…" : "Recompute all"}
                        </button>
                      </div>
                      {Object.entries(extractionResult.floor_heights).map(
                        ([floor, original]) => {
                          const current = editedFloorHeights[floor];
                          const changed =
                            current !== undefined && current !== original;
                          return (
                            <label key={floor} className="twf-result-card-row">
                              <span className="twf-result-card-key">
                                {floor}
                              </span>
                              <span className="twf-fh-input-wrap">
                                <input
                                  type="number"
                                  className={`ds-input ds-input--sm twf-fh-input${changed ? " twf-fh-input--changed" : ""}`}
                                  value={current ?? original ?? ""}
                                  onChange={(e) =>
                                    setEditedFloorHeights((prev) => ({
                                      ...prev,
                                      [floor]:
                                        e.target.value === ""
                                          ? null
                                          : parseFloat(e.target.value),
                                    }))
                                  }
                                  step="0.01"
                                  min="0"
                                />
                                <span className="twf-fh-unit">m</span>
                              </span>
                            </label>
                          );
                        },
                      )}
                    </section>
                  )}

                {extractionResult.legend &&
                  Object.keys(extractionResult.legend).length > 0 && (
                    <section className="ds-card twf-card twf-result-card twf-result-legend-card">
                      <h3 className="ds-section-title">Legend</h3>
                      {Object.entries(extractionResult.legend).map(
                        ([mat, entry]) => (
                          <div
                            key={mat}
                            className="twf-result-card-row twf-result-legend-row"
                          >
                            <HatchSwatch
                              color={entry?.color}
                              hatchFeatures={entry?.hatch_features}
                              imageB64={entry?.swatch_image_b64}
                            />
                            <div className="twf-result-legend-text">
                              <span className="twf-result-card-key">{mat}</span>
                              <span className="twf-result-legend-desc">
                                {entry?.description ?? entry}
                              </span>
                            </div>
                          </div>
                        ),
                      )}
                    </section>
                  )}
              </div>

              {/* Warnings */}
              {extractionResult.warnings &&
                extractionResult.warnings.length > 0 && (
                  <div className="ds-alert ds-alert--warning ds-flush twf-result-warnings">
                    <div className="twf-result-warnings-title">
                      <TriangleAlert size={14} aria-hidden="true" />
                      Warnings ({extractionResult.warnings.length})
                    </div>
                    <ul className="twf-result-warnings-list">
                      {extractionResult.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}

              {/* Summary */}
              {(() => {
                // Prefer the exact legend-title summary; fall back to the canonical
                // material summary for older runs persisted before summary_by_legend.
                const byLegend = extractionResult.summary_by_legend;
                const src =
                  byLegend && Object.keys(byLegend).length > 0
                    ? byLegend
                    : extractionResult.summary;
                if (!src || Object.keys(src).length === 0) return null;
                return (
                  <section className="twf-result-summary">
                    <h3 className="ds-section-title">Summary</h3>
                    <div className="ds-table-wrap">
                      <table className="ds-table twf-result-table">
                        <thead>
                          <tr>
                            <th>
                              <span className="ds-visually-hidden">Swatch</span>
                            </th>
                            <th>Legend</th>
                            <th className="twf-num">Gross m²</th>
                            <th className="twf-num">Net m²</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(src).map(([title, s]) => {
                            const legendEntry = extractionResult.legend?.[title];
                            return (
                              <tr key={title}>
                                <td className="twf-result-summary-swatch">
                                  <HatchSwatch
                                    color={legendEntry?.color}
                                    hatchFeatures={legendEntry?.hatch_features}
                                    imageB64={legendEntry?.swatch_image_b64}
                                    size={18}
                                  />
                                </td>
                                <td>{title}</td>
                                <td className="twf-num">{s.gross_m2?.toFixed(2)}</td>
                                <td className="twf-num">{s.net_m2?.toFixed(2)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </section>
                );
              })()}

            </div>
          )}

          {showCompleted && jobId && (
            <LegendReviewCard jobId={jobId} onRecomputed={handleLegendRecomputed} />
          )}

          {isFetchingFloors && (
            <div className="twf-stream-loader" role="status">
              <LoaderCircle size={16} className="ds-spin" aria-hidden="true" />
              <span>Loading floors…</span>
            </div>
          )}

          {!isFetchingFloors && (
            <section className="twf-floors-section">
              <h3 className="ds-section-title">Detected floors</h3>
              {floors.length === 0 ? (
                <div className="ds-empty twf-empty">
                  No floors were detected in this document — check that it
                  contains labeled floor-plan pages with a matching hatch
                  legend.
                </div>
              ) : (
                <div className="twf-floors-grid">
                  {floors.map((floor, idx) => (
                    <div key={floor.floor_id} className="twf-floor-card">
                      <img
                        src={`data:image/png;base64,${floor.current_mask_b64}`}
                        alt={floor.floor_label}
                        className="twf-floor-img"
                        onClick={() =>
                          openLightbox(
                            floors.map((f) => ({
                              url: `data:image/png;base64,${f.current_mask_b64}`,
                              label: `${f.floor_label} · Page ${f.page_index}`,
                            })),
                            idx,
                          )
                        }
                      />
                      <div className="twf-floor-meta">
                        <span className="twf-floor-label-text">
                          {floor.floor_label}
                        </span>
                        <span className="ds-meta">
                          Page {floor.page_index}
                        </span>
                        {floor.edited && (
                          <span className="ds-badge ds-badge--success">edited</span>
                        )}
                      </div>
                      <button
                        className="ds-btn ds-btn--secondary ds-btn--sm twf-floor-edit-btn"
                        onClick={() => handleOpenFloorInEditor(floor)}
                      >
                        <Pencil size={12} aria-hidden="true" />
                        Edit
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {/* Legend-approval gate: Phase 1 (legend + floor heights, fast) finished
          and is waiting on the user to review/confirm the legend before Phase 2
          (the expensive per-wall CV+VLM classification) starts at all -- avoids
          ever running that expensive step against a legend later discovered to
          be wrong. Same LegendReviewCard component as the post-hoc "edit" mode,
          just in "approve" mode: confirming calls /legend/confirm (not
          /legend/recompute) and hands control back to the main SSE stream for
          Phase 2's own progress. */}
      {phase === "awaiting_legend_approval" && jobId && (
        <LegendReviewCard jobId={jobId} mode="approve" onConfirmed={handleLegendConfirmed} />
      )}
      <ImageLightbox lightbox={lightbox} onClose={closeLightbox} onStep={stepLightbox} />
    </div>
  );
}
